import { Controller } from "@hotwired/stimulus"
import { reformatKeepingCaret, slovakAmount } from "../../utils/caret-format.util"
import { isReachable } from "../../utils/field-visibility.util"
import { childBonusMonths, parseRodneCislo } from "../../utils/rodne-cislo.util"
import { formatAmount } from "../../utils/server-format.util"

// What marks a field's message, so that it is found again to be changed or removed.
const ERROR = "[data-tax-form-error]"
// The "Celý rok" box in front of the other eligible person's months.
const ALL_MONTHS = "[data-action~='change->finance-management--tax-form#toggleAllMonths']"

// The DPFO typ B wizard: shows the parts of a step its answers open, adds and
// removes children, and formats what is typed. It computes nothing of the
// return — every amount of it is worked out on the server; what it works out
// only guides the answers (the months a child can be claimed in, the § 33a
// income limit), and the server checks them again.
//
// What is wrong with a field is said at the field (audit UI-01, UI-02): the
// server's checks come as the step's error summary, whose links name the
// fields; this controller's own come on submit and when a field that was
// typed in is left. Either way the field is marked aria-invalid and described
// by the message, and focus goes to the summary or the first field.
export default class extends Controller {
  static targets = [
    "form",
    // Set to "back" by "Späť", which sends the step along
    "direction",
    // The step's error summary, from the server (_step_errors)
    "errorSummary",
    // The tax year and what a change of it does (step 1)
    "taxYear",
    "yearChangeWarning",
    "yearChangeText",
    // Sections opened by a switch
    "employedFields",
    "rentFields",
    "oslobodenieField",
    "partnerBonusFields",
    "partnerFields",
    "pensionerFields",
    "pensionFields",
    "mortgageFields",
    "twoPercentFields",
    // The account, opened by "Áno" to the payout (XIV. oddiel)
    "bankAccountFields",
    // Children (r. 33)
    "childrenFields",
    "employerBonusReturn",
    "childrenList",
    "child",
    "childTemplate",
    "addChildButton",
    // § 33 ods. 8: where the other person's base is found and what goes with the return, by the kind of return
    "otherPersonIncomeHint",
    "otherPersonAttachments",
    // Parents (§ 50aa, r. 153)
    "parentAFields",
    "parentBFields",
    "parentAdoption",
    // Mortgage co-debtors and the income limit (§ 33a ods. 1 písm. b) a ods. 4)
    "contractYear",
    "coDebtorCheckbox",
    "coDebtorFields",
    "debtorCount",
    "soleDebtorText",
    "coDebtorText",
    "mortgageLimit"
  ]

  static values = {
    maxChildren: { type: Number, default: 20 },
    taxYear: Number,
    // { "2025": "2438.4", … } — one debtor's limit by contract year (TaxForm::Rules.uroky_limity_prijmu)
    mortgageLimits: { type: Object, default: {} },
    // The server's number_to_currency(unit: "EUR", format: "%n %u") as currency_format_data
    amountFormat: { type: Object, default: {} },
    texts: { type: Object, default: {} }
  }

  // The switch's id names the section it opens.
  static SECTIONS = {
    employed: "employedFields",
    rent: "rentFields",
    partner_bonus_na_deti: "partnerBonusFields",
    r032_uplatnujem_na_partnera: "partnerFields",
    r029_poberatel_dochodku: "pensionerFields",
    platil_prispevky_na_dochodok: "pensionFields",
    r035_uplatnuje_uroky: "mortgageFields",
    XIIoddiel_uplatnujem2percenta: "twoPercentFields"
  }

  connect() {
    // Fields typed in: only those are checked when left (audit UI-02).
    this.touched = new WeakSet()
    // The value of a formatted field before its last edit, for a Backspace over a separator (audit UI-38).
    this.previousValues = new WeakMap()
    this.pendingChecks = new Set()
    this.pointerHeld = false
    this.onPointerDown = () => {
      this.pointerHeld = true
    }
    this.onPointerUp = () => this.releasePointer()
    document.addEventListener("pointerdown", this.onPointerDown, true)
    document.addEventListener("pointerup", this.onPointerUp, true)
    document.addEventListener("pointercancel", this.onPointerUp, true)

    this.refreshChildren()
    for (const child of this.childTargets) this.refreshChildMonths(child)
    // A step shown again keeps its months; "Celý rok" says a part of the year from the start, not after a click.
    for (const all of this.element.querySelectorAll(ALL_MONTHS)) this.refreshAllMonths(all.closest("fieldset"))
    // A page the browser restores keeps what was typed; the co-debtors' wording and the limit follow it.
    if (this.hasCoDebtorCheckboxTarget) this.showCoDebtors(this.coDebtorCheckboxTarget.checked)
    this.refreshMortgageLimit()
    if (this.hasTaxYearTarget) this.yearChanged()
    this.applyRestoredChoices()
    if (this.hasDirectionTarget) this.directionTarget.value = ""
    this.showServerErrors()
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.onPointerDown, true)
    document.removeEventListener("pointerup", this.onPointerUp, true)
    document.removeEventListener("pointercancel", this.onPointerUp, true)
    window.cancelAnimationFrame(this.focusFrame)
    window.clearTimeout(this.pendingTimer)
  }

  // --- what is wrong ---------------------------------------------------------

  // The server's checks: each link of the summary marks its field with its
  // message, and the summary takes the focus, so that a screen reader reads it
  // first. A field can have several messages; it shows the first.
  showServerErrors() {
    if (!this.hasErrorSummaryTarget) return

    const marked = new Set()
    for (const link of this.errorSummaryTarget.querySelectorAll("[data-field]")) {
      const field = document.getElementById(link.dataset.field)
      if (!field || marked.has(field)) continue

      marked.add(field)
      this.markField(field, link.textContent.trim(), { source: "server" })
    }
    // After Turbo has rendered the step and scrolled it to the top.
    this.focusFrame = window.requestAnimationFrame(() => {
      const summary = this.errorSummaryTarget
      summary.scrollIntoView?.({ behavior: this.scrollBehavior(), block: "center" })
      summary.focus({ preventScroll: true })
    })
  }

  // A link of the summary: to the field it names, opening what folds it away.
  focusField(event) {
    const field = document.getElementById(event.currentTarget.dataset.field)
    if (!field) return

    event.preventDefault()
    this.focusControl(field)
  }

  // Keeps a step with an empty required field from going to the server, which
  // checks every field again anyway.
  // The form is novalidate, which keeps the browser's unlocalised bubbles
  // away and with them its checks: what the fields declare is checked here and
  // said under the field, and the first one is focused.
  validate(event) {
    const invalid = []
    for (const field of this.formTarget.querySelectorAll("[required], [pattern], [data-format]")) {
      if (field.type === "checkbox" || field.type === "radio" || field.type === "hidden") continue

      const message = this.problemWith(field)
      if (message) {
        this.markField(field, message)
        invalid.push(field)
      } else if (this.errorSource(field) === "client") {
        this.markField(field, null)
      }
    }
    if (invalid.length === 0) return

    event.preventDefault()
    this.focusControl(invalid[0])
  }

  // An empty required field, a value its pattern rejects, a number outside
  // its min and max (the number of debtors), or a rodné číslo that cannot be
  // one — said in the words its data-format names. Closed and folded sections
  // and disabled fields are not checked: they are not sent, or the server
  // checks them.
  problemWith(field) {
    if (field.disabled || !isReachable(field) || field.closest("details:not([open])")) return null

    const format = field.dataset.format
    const message = this.textsValue.formats?.[format] ?? null
    const validity = field.validity ?? {}
    // A number or date field holds "" for what is not a whole number or date (a date typed in part); without a
    // format of its own, such a field is as empty as it reads.
    if (validity.badInput) return message ?? (field.required ? this.textsValue.required : null)

    const value = field.value.trim()
    if (value === "") return field.required ? this.textsValue.required : null
    if (format === "rc") return parseRodneCislo(value) ? null : message

    const wrong = validity.patternMismatch || validity.rangeUnderflow || validity.rangeOverflow || validity.stepMismatch
    return wrong ? message : null
  }

  // A field that was typed in is checked when it is left, and the message is
  // announced (role="alert"). Left with the pointer — on its way to a button,
  // a checkbox, another field —, it is checked once the pointer is released:
  // a message put in during the press shifts the page, and the click lands
  // beside its target.
  checkField(event) {
    const field = event.target
    if (!this.touched.has(field)) return

    if (this.pointerHeld) {
      this.pendingChecks.add(field)
    } else {
      this.checkLeftField(field)
    }
  }

  releasePointer() {
    this.pointerHeld = false
    if (this.pendingChecks.size === 0) return

    const fields = [...this.pendingChecks]
    this.pendingChecks.clear()
    // After the click that follows the release.
    this.pendingTimer = window.setTimeout(() => {
      for (const field of fields) if (field.isConnected) this.checkLeftField(field)
    }, 0)
  }

  checkLeftField(field) {
    const message = this.problemWith(field)
    if (message) {
      if (this.errorMessage(field) !== message) this.markField(field, message, { announce: true })
    } else if (this.errorSource(field) === "client") {
      this.markField(field, null)
    }
  }

  // Typing in a marked field clears its mark once nothing is wrong with it —
  // a server's mark too, the field having been changed; the server checks it
  // again. A radio or a box of a group clears the group's mark. The search box
  // of a searchable select is no field of the step: it sits in the select's
  // .lc-input, and what is typed there took the select's message away while
  // the combobox stayed red; the select's own change clears its mark.
  clearInvalid(event) {
    const field = event.target
    if (!(field instanceof HTMLElement) || !field.matches("input, select, textarea")) return
    if (field.closest(".ss-main, .ss-content")) return

    this.touched.add(field)
    if (!this.errorElement(field) || this.problemWith(field)) return

    this.markField(field, null)
  }

  // Puts `message` at the field — under it, in its check row, or under the
  // legend of its group —, marks the field (aria-invalid, its description
  // including the message's id), or clears all of it when there is none.
  // The message is the shared field error's (shared/_error_form_field), whose
  // colour has a dark value (audit UI-02).
  markField(field, message, { announce = false, source = "client" } = {}) {
    const group = this.groupOf(field)
    // What carries the state: the group, or the field and the combobox a searchable select draws for it. The
    // select keeps it too — a combobox drawn after the server's marks copies it (searchable_select_controller).
    const controls = group ? [group] : [...new Set([field, this.controlOf(field)])]
    const container = group ?? field.closest(".lc-input, .form-check") ?? field.parentElement

    let error = this.errorElement(field)
    if (!message) {
      for (const control of controls) {
        if (error) this.describe(control, error.id, false)
        this.setInvalid(control, false)
      }
      error?.remove()
      return
    }
    // A message said anew when the field is left is put in anew, so that its role="alert" is read out.
    if (announce && error) {
      error.remove()
      error = null
    }

    if (!error) {
      error = this.buildError(field, group)
      if (announce) error.setAttribute("role", "alert")
      this.placeError(error, field, group, container)
    }
    error.dataset.taxFormError = source
    error.querySelector("[data-tax-form-error-text]").textContent = message
    for (const control of controls) {
      this.setInvalid(control, true)
      this.describe(control, error.id, true)
    }
  }

  buildError(field, group) {
    const error = document.createElement("div")
    error.className = "form-hint form-hint--error d-flex mt-1"
    error.id = `${group?.id || field.id || field.name}_error`
    const icon = document.createElement("span")
    icon.className = "material-symbols-outlined fs-6 me-1"
    icon.setAttribute("aria-hidden", "true")
    icon.textContent = "error"
    const text = document.createElement("span")
    text.dataset.taxFormErrorText = ""
    error.append(icon, text)
    return error
  }

  // Under the legend of a group, after the field's input group or searchable
  // select, at the end of a check row.
  placeError(error, field, group, container) {
    if (group) {
      const legend = group.querySelector(":scope > legend")
      legend ? legend.after(error) : group.prepend(error)
      return
    }
    if (container.classList.contains("form-check")) {
      container.append(error)
      return
    }
    const anchor = field.closest(".input-group, [data-controller~='searchable-select']") ?? field
    anchor.after(error)
  }

  // A radio, or a box among several in a fieldset (the months, the spouse's
  // conditions): the message belongs to the group.
  groupOf(field) {
    if (field.type !== "radio" && field.type !== "checkbox") return null

    const fieldset = field.closest("fieldset")
    if (!fieldset) return null
    if (field.type === "checkbox" && fieldset.querySelectorAll("input[type=checkbox]").length < 2) return null
    if (!fieldset.id) fieldset.id = `${field.id || field.name}_group`
    return fieldset
  }

  // What takes the focus and carries the field's state: slim-select hides the
  // select and draws a combobox after it (searchable_select_controller).
  controlOf(field) {
    const sibling = field.nextElementSibling
    return field.tagName === "SELECT" && sibling?.classList.contains("ss-main") ? sibling : field
  }

  errorElement(field) {
    const group = this.groupOf(field)
    if (group) return group.querySelector(`:scope > ${ERROR}`)

    const container = field.closest(".lc-input, .form-check")
    return container?.querySelector(ERROR) ?? null
  }

  errorMessage(field) {
    return this.errorElement(field)?.querySelector("[data-tax-form-error-text]")?.textContent ?? null
  }

  errorSource(field) {
    return this.errorElement(field)?.dataset.taxFormError ?? null
  }

  // aria-invalid and the red border. A group is marked by its message under
  // the legend alone: twelve red months would shout. The combobox of a
  // searchable select gets the class itself — slim-select copies the select's
  // classes when it draws it, but drops a change made while it updates the
  // value, so a picked value left it red.
  setInvalid(control, invalid) {
    if (control.tagName === "FIELDSET") return

    control.classList.toggle("is-invalid", invalid)
    if (invalid) {
      control.setAttribute("aria-invalid", "true")
    } else {
      control.removeAttribute("aria-invalid")
    }
  }

  // Adds or drops one id of aria-describedby, keeping the hints' ids.
  describe(element, id, add) {
    const ids = (element.getAttribute("aria-describedby") ?? "").split(/\s+/).filter((each) => each && each !== id)
    if (add) ids.push(id)
    if (ids.length > 0) {
      element.setAttribute("aria-describedby", ids.join(" "))
    } else {
      element.removeAttribute("aria-describedby")
    }
  }

  // Scrolls to a field and focuses it — the first enabled box of its group
  // when it is a disabled one (a month the child cannot be claimed in). A
  // disabled field outside a group (a switch the step disabled) is only
  // scrolled to.
  focusControl(field) {
    const folded = field.closest("details:not([open])")
    if (folded) folded.open = true

    let target = this.controlOf(field)
    if (target.disabled) {
      const others = field.closest("fieldset, .child-entry")?.querySelectorAll("input:not([disabled])") ?? []
      target = [...others].find(isReachable) ?? target
    }
    const area = field.closest(".lc-input, .form-check, fieldset") ?? target
    area.scrollIntoView?.({ behavior: this.scrollBehavior(), block: "center" })
    target.focus({ preventScroll: true })
  }

  scrollBehavior() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
  }

  // --- the steps ---------------------------------------------------------------

  // "Späť" is a link that works without JavaScript; with it, the step is sent
  // along first (direction=back) so that what was typed on it is kept. It is
  // not checked here: the server keeps it either way and marks a step that
  // fails its checks, which the preview then leads back to.
  goBack(event) {
    if (!this.hasFormTarget || !this.hasDirectionTarget) return

    event.preventDefault()
    this.directionTarget.value = "back"
    this.formTarget.submit()
    // submit() has taken the form's entries; a page the browser keeps (a navigation stopped) or brings back must not
    // go back again on the next "Ďalej" (line review F4).
    this.directionTarget.value = ""
  }

  // A switch, an answer or the other person's kind the browser restored with the page (Firefox does on a reload) opens
  // what it opens: the server drew the sections from the saved draft (line review F7).
  applyRestoredChoices() {
    const handlers = ["toggleSection", "toggleOslobodenie", "toggleChildren", "toggleChildMonths", "togglePayout",
      "toggleParents", "otherPersonIncomeTypeChanged"]
    for (const control of this.element.querySelectorAll("input[data-action], select[data-action]")) {
      if (control.type === "radio" && !control.checked) continue

      const actions = control.dataset.action.split(/\s+/)
      for (const handler of handlers) {
        if (actions.some((action) => action.endsWith(`->${this.identifier}#${handler}`))) this[handler]({ target: control })
      }
    }
  }

  // Another tax year starts the draft again (TaxFormController#save_personal_info): said under the select as soon
  // as one is picked, before anything is sent (audit UI-25).
  yearChanged() {
    if (!this.hasTaxYearTarget || !this.hasYearChangeWarningTarget) return

    const year = this.taxYearTarget.value
    const changed = year !== String(this.taxYearValue)
    this.yearChangeWarningTarget.classList.toggle("d-none", !changed)
    if (this.hasYearChangeTextTarget) {
      this.yearChangeTextTarget.textContent = changed ? (this.textsValue.yearChange ?? "").replace("%{year}", year) : ""
    }
    this.describe(this.taxYearTarget, this.yearChangeWarningTarget.id, changed)
  }

  // A switch opens or closes the section its id names, and says so (aria-expanded; audit UI-32).
  toggleSection(event) {
    const target = this.constructor.SECTIONS[event.target.id]
    if (target && this[`has${this.capitalize(target)}Target`]) {
      this[`${target}Target`].classList.toggle("d-none", !event.target.checked)
    }
    this.syncExpanded(event.target)
  }

  toggleOslobodenie(event) {
    if (this.hasOslobodenieFieldTarget) {
      this.oslobodenieFieldTarget.classList.toggle("d-none", !event.target.checked)
    }
    this.syncExpanded(event.target)
  }

  syncExpanded(control) {
    if (control.hasAttribute("aria-expanded")) control.setAttribute("aria-expanded", String(control.checked))
  }

  // "Áno" opens the children; the other two answers file none, and with a
  // bonus the employer already paid ask to confirm it is returned.
  toggleChildren(event) {
    const yes = event.target.value === "yes"
    if (this.hasChildrenFieldsTarget) this.childrenFieldsTarget.classList.toggle("d-none", !yes)
    if (this.hasEmployerBonusReturnTarget) this.employerBonusReturnTarget.classList.toggle("d-none", yes)
  }

  // "Áno" to the payout asks for the account; "Nie" leaves it out.
  togglePayout(event) {
    if (this.hasBankAccountFieldsTarget) {
      this.bankAccountFieldsTarget.classList.toggle("d-none", event.target.value !== "1")
    }
  }

  // "Celý rok" makes the single months irrelevant.
  toggleChildMonths(event) {
    const months = event.target.closest(".child-entry")?.querySelector("[data-child-months]")
    if (months) months.classList.toggle("d-none", event.target.checked)
  }

  syncChildMonths(event) {
    const entry = event.target.closest(".child-entry")
    if (entry) this.refreshChildMonths(entry)
  }

  // With a valid rodné číslo, only the months the child carries a bonus in
  // can be ticked, and they are named: "Celý rok" goes for a child born or
  // turning 18 in the year — when it was ticked, every month the child has is
  // ticked instead (audit F15). Without one, every month is open again.
  refreshChildMonths(entry) {
    const rodneCislo = entry.querySelector("[data-child-rodne-cislo]")
    const wholeYear = entry.querySelector("[data-child-whole-year]")
    const monthsBox = entry.querySelector("[data-child-months]")
    if (!rodneCislo || !wholeYear || !monthsBox) return

    const boxes = [...monthsBox.querySelectorAll("input[type=checkbox]")]
    // Nine digits are a number issued before 1954 — never a child's — and every ten-digit number passes through them
    // while it is typed: read as no number yet, so that a keystroke does not clear the months ticked (line review F1).
    const digits = rodneCislo.value.replace(/\D/g, "")
    const eligible = digits.length === 10 ? childBonusMonths(rodneCislo.value, this.taxYearValue) : null
    const partYear = eligible !== null && !eligible.every(Boolean)
    if (partYear && wholeYear.checked) {
      wholeYear.checked = false
      boxes.forEach((box, index) => {
        box.checked = eligible[index]
      })
      monthsBox.classList.remove("d-none")
    }
    wholeYear.disabled = partYear
    boxes.forEach((box, index) => {
      box.disabled = partYear && !eligible[index]
      if (box.disabled) box.checked = false
    })
    this.showChildRange(entry.querySelector("[data-child-range]"), partYear ? eligible : null)
  }

  showChildRange(range, eligible) {
    if (!range) return

    let text = ""
    if (eligible) {
      const first = eligible.indexOf(true)
      const names = this.textsValue.monthNames ?? []
      text =
        first < 0
          ? (this.textsValue.childNoMonths ?? "")
          : (this.textsValue.childMonths ?? "")
              .replace("%{from}", names[first] ?? "")
              .replace("%{to}", names[eligible.lastIndexOf(true)] ?? "")
    }
    range.textContent = text
    range.classList.toggle("d-none", text === "")
  }

  // The other eligible person's months have no whole-year answer: "Celý rok"
  // ticks or clears the twelve boxes, and follows them (audit UI-31).
  toggleAllMonths(event) {
    const months = event.target.closest("fieldset")?.querySelectorAll(".tax-form-months input[type=checkbox]") ?? []
    for (const box of months) if (!box.disabled) box.checked = event.target.checked
  }

  syncAllMonths(event) {
    if (event.target.matches(ALL_MONTHS)) return

    this.refreshAllMonths(event.target.closest("fieldset"))
  }

  // "Celý rok" ticked for all twelve months, mixed for some, clear for none.
  refreshAllMonths(fieldset) {
    const all = fieldset?.querySelector(ALL_MONTHS)
    if (!all) return

    const boxes = [...fieldset.querySelectorAll(".tax-form-months input[type=checkbox]")]
    const ticked = boxes.filter((box) => box.checked).length
    all.checked = ticked === boxes.length
    all.indeterminate = ticked > 0 && ticked < boxes.length
  }

  // § 33 ods. 8: the base and the attachments follow how the other person
  // settled the year (audit UI-31).
  otherPersonIncomeTypeChanged(event) {
    const texts = this.textsValue.otherPerson ?? {}
    const kind = event.target.value
    const pick = (map) => map?.[kind] ?? map?.[""] ?? ""
    if (this.hasOtherPersonIncomeHintTarget) this.otherPersonIncomeHintTarget.textContent = pick(texts.hints)
    if (this.hasOtherPersonAttachmentsTarget) this.otherPersonAttachmentsTarget.textContent = pick(texts.attachments)
  }

  // Co-debtors open their number and word the age and income conditions for
  // every debtor (§ 33a ods. 4).
  toggleCoDebtors(event) {
    this.showCoDebtors(event.target.checked)
    this.syncExpanded(event.target)
    this.refreshMortgageLimit()
  }

  showCoDebtors(coDebtors) {
    if (this.hasCoDebtorFieldsTarget) this.coDebtorFieldsTarget.classList.toggle("d-none", !coDebtors)
    for (const text of this.soleDebtorTextTargets) text.classList.toggle("d-none", coDebtors)
    for (const text of this.coDebtorTextTargets) text.classList.toggle("d-none", !coDebtors)
  }

  // The income limit for the year of the contract date entered, times the
  // number of debtors — the server renders the same sentence for the saved
  // answers.
  refreshMortgageLimit() {
    if (!this.hasMortgageLimitTarget) return

    const date = this.hasContractYearTarget ? this.contractYearTarget.value.trim() : ""
    const year = date.match(/^(\d{4})-\d{2}-\d{2}$/)?.[1] ?? ""
    const limit = this.mortgageLimitsValue[year]
    const coDebtors = this.hasCoDebtorCheckboxTarget && this.coDebtorCheckboxTarget.checked
    const count = coDebtors ? this.debtorCount() : 1

    let text = this.textsValue.mortgageLimitUnknown ?? ""
    if (limit !== undefined && count !== null) {
      const total = formatAmount((Math.round(Number(limit) * 100) * count) / 100, this.amountFormatValue)
      text = (coDebtors ? this.textsValue.mortgageLimitCoDebtors : this.textsValue.mortgageLimit) ?? ""
      text = text.replace("%{year}", year).replace("%{count}", String(count)).replace("%{limit}", total)
    }
    this.mortgageLimitTarget.textContent = text
  }

  // The number of debtors typed, within the field's own min and max; null otherwise.
  debtorCount() {
    if (!this.hasDebtorCountTarget) return null

    const input = this.debtorCountTarget
    const count = Number(input.value.trim())
    const inRange = Number.isInteger(count) && count >= Number(input.min) && count <= Number(input.max)
    return input.value.trim() !== "" && inRange ? count : null
  }

  toggleParents(event) {
    const choice = event.target.value
    if (this.hasParentAFieldsTarget) this.parentAFieldsTarget.classList.toggle("d-none", choice === "nie")
    if (this.hasParentBFieldsTarget) this.parentBFieldsTarget.classList.toggle("d-none", choice !== "obidvom")
    if (this.hasParentAdoptionTarget) this.parentAdoptionTarget.classList.toggle("d-none", choice === "nie")
  }

  // --- children ------------------------------------------------------------------

  // A blank child is cloned from the server-rendered <template>; only the
  // index in its name/id/for/aria-controls attributes is rewritten. Indexes
  // only grow, so a removed child never hands its index to a new one; the
  // server orders the rows by it. The new child's name takes the focus
  // (audit UI-32).
  addChild() {
    if (!this.hasChildTemplateTarget || !this.hasChildrenListTarget) return
    if (this.childTargets.length >= this.maxChildrenValue) return

    const index = String(this.nextChildIndex())
    const fragment = this.childTemplateTarget.content.cloneNode(true)
    fragment.querySelectorAll("[name], [id], [for], [aria-controls], [aria-describedby]").forEach((element) => {
      for (const attribute of ["name", "id", "for", "aria-controls", "aria-describedby"]) {
        const current = element.getAttribute(attribute)
        if (current) element.setAttribute(attribute, current.replaceAll("__INDEX__", index))
      }
    })

    const entry = fragment.firstElementChild
    this.childrenListTarget.appendChild(fragment)
    this.refreshChildren()
    entry?.querySelector("[data-child-name]")?.focus()
  }

  // The focus goes to the remove button of the child before (or after) the
  // one removed, or to "Pridať dieťa" — not to the page, with the button that
  // held it gone (audit UI-32).
  // One past the highest children[N] on the page. A page Turbo restores from its snapshot keeps the gap a removed
  // child left (children 1 and 2, no 0), where the number of children would reuse a live index (line review F2).
  nextChildIndex() {
    const indexes = this.childTargets.flatMap((child) =>
      [...child.querySelectorAll("[name^='children[']")].map((field) => Number(field.name.match(/^children\[(\d+)\]/)?.[1]))
    )
    return Math.max(-1, ...indexes.filter(Number.isInteger)) + 1
  }

  removeChild(event) {
    const entry = event.target.closest(".child-entry")
    if (!entry) return

    const children = this.childTargets
    const position = children.indexOf(entry)
    const neighbour = children[position - 1] ?? children[position + 1]
    entry.remove()
    this.refreshChildren()

    const remove = neighbour?.querySelector("[data-child-remove]")
    const next = remove && !remove.disabled ? remove : this.hasAddChildButtonTarget ? this.addChildButtonTarget : null
    next?.focus()
  }

  // Numbers the children by position — their heading and the name of their
  // remove button — and stops adding at the schema's limit.
  refreshChildren() {
    const title = this.textsValue.childTitle
    const removeLabel = this.textsValue.removeChild
    this.childTargets.forEach((child, position) => {
      const heading = child.querySelector("[data-child-title]")
      if (heading && title) heading.textContent = title.replace("%{number}", position + 1)
      const remove = child.querySelector("[data-child-remove]")
      if (remove && removeLabel) {
        const label = removeLabel.replace("%{number}", position + 1)
        remove.setAttribute("aria-label", label)
        remove.dataset.tooltipTitleValue = label
      }
    })
    if (this.hasAddChildButtonTarget) {
      this.addChildButtonTarget.disabled = this.childTargets.length >= this.maxChildrenValue
    }
    // The last card stays: "Nie" is the way to claim no child.
    const lonely = this.childTargets.length <= 1
    this.childTargets.forEach((child) => {
      const remove = child.querySelector("[data-child-remove]")
      if (remove) remove.disabled = lonely
    })
  }

  // --- what Lucanto offers -----------------------------------------------------------

  // Puts what Lucanto offers into the fields it names — the figure it holds
  // now for a value still marked as prefilled, the address it holds for an
  // empty one — and drops the offer. An offer's hidden flag goes along, so the
  // server keeps the value's marker once it finds the same figure on save.
  applySuggestion(event) {
    const offer = event.currentTarget.closest("[data-suggestion]")
    let first = null
    for (const [name, value] of Object.entries(event.params.fields ?? {})) {
      const field = this.formTarget.elements.namedItem(name)
      if (!field) continue

      field.value = field.dataset?.format === "amount" ? (slovakAmount(value) ?? value) : value
      this.previousValues.set(field, field.value)
      this.markField(field, null)
      first ??= field
    }
    if (!offer) return

    for (const flag of offer.querySelectorAll("input[type=hidden][disabled]")) flag.disabled = false
    offer.classList.add("d-none")
    // The button just pressed is gone: focus goes to the field it filled, which a screen reader then reads with its
    // new value, rather than to the page (line review F5, M1).
    first?.focus()
  }

  // --- formatting ------------------------------------------------------------------

  // PSČ: five digits, a space after the third.
  formatPsc(event) {
    this.reformat(event, /\d/, (digits) => {
      const kept = digits.slice(0, 5)
      return kept.length > 3 ? `${kept.slice(0, 3)} ${kept.slice(3)}` : kept
    })
  }

  // Rodné číslo: ten digits at most, " / " after the sixth.
  formatRodneCislo(event) {
    this.reformat(event, /\d/, (digits) => {
      const kept = digits.slice(0, 10)
      return kept.length > 6 ? `${kept.slice(0, 6)} / ${kept.slice(6)}` : kept
    })
  }

  // IBAN: letters and digits in groups of four, upper case.
  formatIban(event) {
    this.reformat(
      event,
      /[A-Za-z0-9]/,
      (characters) =>
        characters
          .toUpperCase()
          .match(/.{1,4}/g)
          ?.join(" ") ?? ""
    )
  }

  // An amount: only what can be part of one is kept — digits, spaces (a
  // pasted no-break one becomes a plain one) and the decimal comma or point;
  // the field's own check says when the rest is not an amount. It is written
  // in the Slovak shape once the field is left (normalizeAmount), not while it
  // is typed.
  formatCurrency(event) {
    this.reformat(event, /[\d\s,.]/, (characters) => characters.replace(/\s/g, " "))
  }

  // "1234.5" becomes "1 234,50" — what the step shows for a saved amount (audit UI-13).
  normalizeAmount(event) {
    const formatted = slovakAmount(event.target.value)
    if (formatted === null) return

    event.target.value = formatted
    // The next Backspace starts from this value, not from the one typed before it (line review F3).
    this.previousValues.set(event.target, formatted)
  }

  reformat(event, significant, format) {
    const input = event.target
    reformatKeepingCaret(input, {
      significant,
      format,
      previous: this.previousValues.get(input) ?? input.defaultValue,
      inputType: event.inputType
    })
    this.previousValues.set(input, input.value)
  }

  capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }
}
